import React, { useEffect, useMemo, useState } from "react";
import {
    Field,
    TagPicker,
    TagPickerList,
    TagPickerInput,
    TagPickerControl,
    TagPickerProps,
    TagPickerOption,
    TagPickerGroup,
    TagPickerOptionGroup,
    makeStyles,
    mergeClasses,
    tokens,
    InteractionTag,
    InteractionTagPrimary,
    InteractionTagSecondary,
    Spinner,
} from "@fluentui/react-components";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { useTheme } from "../../hooks/useTheme";

const useStyles = makeStyles({
    group: {
        "& > span": {
            color: tokens.colorBrandForeground2,
        },
        "&:not(:last-child)::after": {
            display: "none",
            borderBottom: "none",
        },
    },
    option: {
        paddingLeft: "20px",
    },
    optionText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    menuLabel: {
        color: tokens.colorNeutralForeground4,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase200,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalSNudge}`,
    },
    list: {
        maxHeight: "32vh",
    },
    tag: {
        maxWidth: "100%",
        minWidth: 0,
        "& span": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
    },
    controlBase: {
        "&&&": {
            "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        },
        // Fix: mobile webviews don't render the blinking caret inside inputs
        // within a fixed-position overlay. A compositing layer forces proper rendering.
        "& input": {
            transform: "translateZ(0)",
            "caret-color": "auto",
        },
    },
    controlHover: {
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    inputCollapsed: {
        flex: "0 0 0px",
        minWidth: "0px",
        padding: "0",
    },
    statusMessage: {
        padding: "8px",
        fontStyle: "italic",
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground4,
        display: "block",
    },
    headerMessage: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalSNudge}`,
        fontStyle: "italic",
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground4,
        display: "block",
    },
    spinner: {
        padding: "8px",
        justifyContent: "flex-start",
    },
    "field-4__validationMessage": {
        color: tokens.colorStatusDangerForeground1,
    },
});

export interface TagData {
    key: string;
    label: string;
    group?: TagGroupData;
}

export interface TagGroupData {
    key: string;
    label: string;
}

interface SingleSelectTagPickerProps extends LocalizeContextProps {
    label: string;
    name: string;
    options: TagData[];
    selectedOption?: TagData;
    required?: boolean;
    disabled?: boolean;
    limit?: number;
    placeholder?: string;
    noOptionsLabel?: string;
    hideNoOptions?: boolean;
    limitExceededLabel?: string;
    resetToken?: number;
    validationMessage?: string;
    isLoading?: boolean;
    statusMessage?: string;
    keepQueryOnClose?: boolean;
    headerMessage?: string;
    onOptionSelect: (data?: TagData) => void;
    onQueryChange?: (query: string) => void;
}

export const SingleSelectTagPicker = withLocalize(
    (props: SingleSelectTagPickerProps): JSX.Element => {
        const styles = useStyles();
        const { isDarkMode } = useTheme();

        const [query, setQuery] = useState<string>("");
        const [open, setOpen] = useState<boolean>(false);

        const componentId = useMemo(() => `${props.name}-single-select-tag-picker`, [props.name]);

        // disableAutoFocus is supported by the underlying combobox but not exposed in TagPickerProps.
        // Without it, the first option is invisibly focused on open, causing Down Arrow to skip to the second option.
        const disableAutoFocus = useMemo(() => {
            return {
                disableAutoFocus: query ? false : true,
            } as Partial<TagPickerProps>;
        }, [query]);

        const selectedOptions = useMemo(() => (props.selectedOption ? [props.selectedOption.key] : []), [
            props.selectedOption,
        ]);

        useEffect(() => {
            setQuery("");
        }, [props.resetToken]);

        const filteredOptions = useMemo(() => {
            if (!query) return props.options;

            return props.options.filter((option) =>
                option.label.toLocaleLowerCase().includes(query.toLocaleLowerCase())
            );
        }, [props.options, query]);

        const limitedOptions = useMemo(() => {
            if (!props.limit) return filteredOptions;

            return filteredOptions.slice(0, props.limit);
        }, [filteredOptions]);

        const groupedOptions = useMemo(() => {
            const grouped = limitedOptions.reduce<Record<string, TagData[]>>((acc, option) => {
                const key = option.group?.key ?? "";

                acc[key] = acc[key] ?? [];
                acc[key].push(option);

                return acc;
            }, {});

            return Object.values(grouped);
        }, [limitedOptions]);

        const onOptionSelect: TagPickerProps["onOptionSelect"] = (e: any, data: any) => {
            const selectedTag =
                props.selectedOption?.key === data.value
                    ? undefined
                    : props.options.find((option) => option.key === data.value);

            setQuery("");
            props.onOptionSelect(selectedTag);
        };

        const onPickerOpenChange = (e: any, data: any) => {
            if (!props.selectedOption) {
                setOpen(data.open);

                if (!data.open && !props.keepQueryOnClose) {
                    setQuery("");
                    props.onQueryChange?.("");
                }
            }
        };

        const onInputChange = (e: any) => {
            if (!props.selectedOption) {
                const value = e.target.value;
                setQuery(value);
                props.onQueryChange?.(value);
            }
        };

        const RenderLimitLabel = () => {
            const limitLabel = RenderLimitLabelText();
            if (limitLabel) {
                return <span className={styles.menuLabel}>{limitLabel}</span>;
            }
        };

        const RenderLimitLabelText = () => {
            if (limitedOptions.length <= 0) return props.hideNoOptions ? "" : props.noOptionsLabel ?? "";

            if (props.limit && props.limitExceededLabel && filteredOptions.length !== limitedOptions.length) {
                const parts = props.limitExceededLabel.split("[[search]]");

                if (parts.length === 2) {
                    return (
                        <>
                            {parts[0]}
                            <strong>{props.translate("SHARED.FIELD.PROJECT_LIMIT_EXCEEDED.SEARCH").toString()}</strong>
                            {parts[1]}
                        </>
                    );
                }
            }

            return "";
        };

        const RenderGroupedOptions = () => {
            if (!query && groupedOptions[0]?.[0].group) {
                return groupedOptions.map((group, index) => (
                    <TagPickerOptionGroup
                        data-testid={`${componentId}-group-${group[0].group?.key}-${index}`}
                        label={group[0].group?.label}
                        key={`${group[0].group?.key} - ${index}`}
                        className={styles.group}
                    >
                        {RenderOptions(group)}
                    </TagPickerOptionGroup>
                ));
            }

            return RenderOptions(limitedOptions);
        };

        const RenderOptions = (options: TagData[]) => {
            return options.map((option) => (
                <TagPickerOption
                    data-testid={`${componentId}-option-${option.key}`}
                    value={option.key}
                    key={option.key}
                    text={option.label}
                    className={styles.option}
                    title={option.label}
                >
                    <span className={styles.optionText}>{option.label}</span>
                </TagPickerOption>
            ));
        };

        return (
            <Field
                data-testid={`${componentId}-field`}
                label={props.label}
                required={props.required}
                validationMessage={props.validationMessage ?? ""}
            >
                <TagPicker
                    data-testid={`${componentId}-picker`}
                    onOptionSelect={onOptionSelect}
                    onOpenChange={onPickerOpenChange}
                    selectedOptions={selectedOptions}
                    disabled={props.disabled ?? false}
                    appearance={isDarkMode ? "filled-darker" : "outline"}
                    open={open}
                    {...disableAutoFocus}
                >
                    <TagPickerControl
                        className={mergeClasses(styles.controlBase, !(props.disabled ?? false) && styles.controlHover)}
                        style={{
                            minWidth: "0px",
                            ...(props.selectedOption ? { flexWrap: "nowrap", overflow: "hidden" } : {}),
                        }}
                        expandIcon={props.selectedOption ? null : undefined}
                    >
                        {props.selectedOption && (
                            <TagPickerGroup aria-label={`Selected ${props.name} Options`}>
                                <InteractionTag
                                    data-testid={`${componentId}-selected-option`}
                                    key={props.selectedOption.key}
                                    shape="rounded"
                                    value={props.selectedOption.key}
                                    className={styles.tag}
                                    title={props.selectedOption.label}
                                    size="small"
                                    appearance={isDarkMode ? "outline" : "filled"}
                                >
                                    <InteractionTagPrimary
                                        hasSecondaryAction
                                        data-testid={`${componentId}-selected-option-primary`}
                                    >
                                        {props.selectedOption.label}
                                    </InteractionTagPrimary>
                                    <InteractionTagSecondary
                                        aria-label="Remove"
                                        data-testid={`${componentId}-remove-selected-option`}
                                    />
                                </InteractionTag>
                            </TagPickerGroup>
                        )}
                        <TagPickerInput
                            data-testid={`${componentId}-input`}
                            aria-label={props.label}
                            placeholder={props.selectedOption ? "" : props.placeholder}
                            value={query}
                            onChange={onInputChange}
                            className={props.selectedOption ? styles.inputCollapsed : undefined}
                            autoComplete="off"
                            title=""
                        />
                    </TagPickerControl>
                    <TagPickerList className={styles.list}>
                        {props.headerMessage && (
                            <span className={styles.headerMessage} data-testid={`${componentId}-header-message`}>
                                {props.headerMessage}
                            </span>
                        )}
                        {RenderLimitLabel()}
                        {RenderGroupedOptions()}
                        {props.isLoading && (
                            <Spinner
                                data-testid={`${componentId}-spinner`}
                                size="tiny"
                                label={props.statusMessage || props.translate("APP.LOADING_PROJECTS").toString()}
                                className={styles.spinner}
                            />
                        )}
                        {!props.isLoading && props.statusMessage && (
                            <span className={styles.statusMessage} data-testid={`${componentId}-status-message`}>
                                {props.statusMessage}
                            </span>
                        )}
                    </TagPickerList>
                </TagPicker>
            </Field>
        );
    }
);
